import { useEffect } from "react";

/**
 * Client-side SEO helper for a SPA. Sets title, description, canonical,
 * Open Graph tags and (optionally) a noindex robots directive without
 * introducing an extra framework dependency (no react-helmet).
 *
 * Existing manually maintained SEO on admin fields is respected — only
 * overwrites the DOM head for the current page.
 */
const CANONICAL_ORIGIN = "https://teknikdiesel.com";

function upsertMeta(selector, attr, name, content) {
  if (content == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function canonicalUrl(path = "/") {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${CANONICAL_ORIGIN}${clean}`;
}

export default function useSEO({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;

    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    }
    upsertMeta('meta[property="og:type"]', "property", "og:type", type);
    if (image) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", image);
    }
    const url = path ? canonicalUrl(path) : null;
    if (url) {
      upsertLink("canonical", url);
      upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    }

    const robotsEl = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      upsertMeta('meta[name="robots"]', "name", "robots", "noindex, nofollow");
    } else if (robotsEl) {
      robotsEl.remove();
    }
  }, [title, description, path, image, type, noindex]);
}
